import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  GbstandardrechtService,
  LastRechtGruppeId,
  LoadStandardrechtSichtByLRGruppen,
  PersonStammMasterId,
  RechtMasterListEntry,
  RechtMasterListEntryMeta,
  UebrigeBerechtigungenListEntryMeta,
  UebrigeBerechtigungenResponseMeta
} from '../../../../core/gb-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { getQueryParams } from '../../../../grundbuch/recht/recht.functions';
import { LinkComponent, NavTarget } from '../../../../shared/components/link/link.component';
import { Subscription } from 'rxjs';
import { MapResponse } from '../../../../core/maps-api';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { HistoryMode } from 'app/core/services/HistoryMode';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { ListLoader } from 'app/shared/components/list-loader/list-loader';
import { ListLoaderPaginationBuilder } from 'app/shared/components/list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from 'app/shared/components/list-loader/pagination-lookup-result';
import { AbstractComponent } from 'app/shared/components/abstract-component/abstract.component';
import { ListLoaderPreloaded } from 'app/shared/components/list-loader/list-loader-preloaded';
import { ListLoaderWrapperComponent } from '../../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { KompaktPersongbComponent } from '../../../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { HistoryDirective } from '../../../../shared/directives/history.directive';
import { RechtListBeschreibungComponent } from '../../../../shared/components/recht-list-beschreibung/recht-list-beschreibung.component';
import { KompaktGrundstueckComponent } from '../../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { DecimalHochkommaPipe } from '../../../../shared/pipes/simple/decimalHochkomma.pipe';
import { FormatRechteIdPipe } from '../../../../shared/pipes/format/format-rechte-id.pipe';
import ArtEnum = RechtMasterListEntryMeta.ArtEnum;

@Component({
  selector: 'cap-personstamm-uebrige-berechtigungen',
  templateUrl: './personstamm-uebrige-berechtigungen.component.html',
  styleUrls: ['./personstamm-uebrige-berechtigungen.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    TranslatePipe,
    KompaktPersongbComponent,
    HistoryDirective,
    LinkComponent,
    RechtListBeschreibungComponent,
    KompaktGrundstueckComponent,
    DecimalHochkommaPipe,
    FormatRechteIdPipe
  ]
})
export class PersonstammUebrigeBerechtigungenComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  personStammMasterId: PersonStammMasterId;

  @Input()
  mitHist = true;

  listLoader: ListLoader<ViewModelUebrigeBerechtigungenListEntry>;

  preloadedIds: Array<LastRechtGruppeId>;

  response: UebrigeBerechtigungenResponseMeta;
  viewModelUebrigeBerechtigungen: Array<ViewModelUebrigeBerechtigungenListEntry>;
  pagedViewModelUebrigeBerechtigungen: Array<ViewModelUebrigeBerechtigungenListEntry>;

  private readonly PAGE_SIZE = 30;
  private readonly CURRENT_PAGE = 1;

  navTargets = NavTarget;

  private subscription = new Subscription();

  constructor(
    private appConfigService: AppConfigService,
    protected appformatConfigService: FormatConfigService,
    public translate: TranslateService,
    private timepointService: TimepointService,
    private standardrechtService: GbstandardrechtService
  ) {
    super();
    keysToTranslate<string>([]);
  }

  ngOnInit(): void {
    this.viewModelUebrigeBerechtigungen = [];
    this.subscription.add(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        if (this.viewModelUebrigeBerechtigungen) {
          if (!this.personStammMasterId) {
            this.viewModelUebrigeBerechtigungen = new Array<ViewModelUebrigeBerechtigungenListEntry>();
            this.pagedViewModelUebrigeBerechtigungen = new Array<ViewModelUebrigeBerechtigungenListEntry>();
            this.listLoader = new ListLoaderPreloaded(15, 1, []);
            return;
          }
          const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
          this.standardrechtService.loadLRGruppenIdsSichtPersonstamm(this.personStammMasterId.id, historisierung).subscribe((response) => {
            this.preloadedIds = response.lrGruppen.map((id) => id.lastRechtGruppeId);
            this.loadUebrigeBerechtigte();
          });
        }
      })
    );
  }

  private loadUebrigeBerechtigte() {
    const observable = (offset, limit, sortColumn) => this.createObservable(offset, limit);
    this.listLoader = ListLoaderPaginationBuilder.builder<ViewModelUebrigeBerechtigungenListEntry>()
      .setNoSortModel(true)
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(this.listLoader);
  }

  private createObservable(offset: number, limit: number) {
    const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
    let request: LoadStandardrechtSichtByLRGruppen = {
      personStammMasterId: this.personStammMasterId,
      lastRechtGruppeIds: this.preloadedIds,
      historyData: { historyMode: historisierung, viewTimeStamp: this.timepointService.timepointCtx.timepoint },
      limit: limit,
      offset: offset
    };
    return this.standardrechtService.loadStandardrechtSichtLRGGruppen(request).pipe(
      map((data) => {
        const uebrigeBerechtigungenVMs: Array<ViewModelUebrigeBerechtigungenListEntry> = [];
        data.nichtRelevanteLastRechtGruppen.forEach((g) => {
          const index = this.preloadedIds.map((id) => id.id).indexOf(g.id, offset);
          if (index > -1) {
            this.preloadedIds.splice(index, 1);
          }
        });
        data.results.forEach((entry) => uebrigeBerechtigungenVMs.push(new ViewModelUebrigeBerechtigungenListEntry(entry)));
        return <PaginationLookupResult<ViewModelUebrigeBerechtigungenListEntry>>{
          result: uebrigeBerechtigungenVMs,
          totalItems: this.preloadedIds.length
        };
      })
    );
  }

  getQueryParamsRecht(version: RechtMasterListEntry) {
    return getQueryParams(version);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getDetailNavigationFor(art: RechtMasterListEntryMeta.ArtEnum) {
    if (!art) {
      return NavTarget.NAV_GRUNDSTUECK;
    }
    switch (art) {
      case ArtEnum.ANMERKUNG:
        return NavTarget.NAV_GRUNDSTUECK_ANMERKUNGEN;
      case ArtEnum.DIENSTBARKEIT:
        return NavTarget.NAV_GRUNDSTUECK_DIENSTBARKEITEN;
      case ArtEnum.GRUNDLAST:
        return NavTarget.NAV_GRUNDSTUECK_GRUNDLASTEN;
      case ArtEnum.EIGENTUM_ANTEIL:
        return NavTarget.NAV_GRUNDSTUECK_EIGENTUM;
      case ArtEnum.PFANDRECHT:
        return NavTarget.NAV_GRUNDSTUECK_PFANDRECHTE;
      case ArtEnum.SDR_DIENSTBARKEIT:
        return NavTarget.NAV_GRUNDSTUECK_DIENSTBARKEITEN;
      case ArtEnum.VORMERKUNG:
        return NavTarget.NAV_GRUNDSTUECK_VORMERKUNGEN;
      default:
        return NavTarget.NAV_GRUNDSTUECK;
    }
  }
}

class ViewModelUebrigeBerechtigungenListEntry {
  mapLinkItem: MapResponse;
  model: UebrigeBerechtigungenListEntryMeta;

  constructor(berechtigterEntry: UebrigeBerechtigungenListEntryMeta) {
    this.model = berechtigterEntry;
    this.mapLinkItem = <MapResponse>{ url: null };
  }
}
